// `FridayReport` (13 §Shapes, §Friday report): the frozen snapshot in `friday_report.snapshot`.
// Client-visibility rows only, enforced by `ClientScorecard` and closed (strict) objects.
import { z } from "zod";
import { Band, ContextSourceKind, Currency, RecipientKind } from "./enums";
import { DottedName, Email, IsoDate, Timestamp, Uuid, WeekKey } from "./primitives";
import { ClientScorecard } from "./scorecard";

export const ReportRecipient = z
  .object({ email: Email, membership_id: Uuid.optional(), kind: RecipientKind })
  .meta({ id: "ReportRecipient" });
export type ReportRecipient = z.infer<typeof ReportRecipient>;

/** What the generator read (agent-os.md §12.10, the visible-sources rule). */
export const ContextSource = z
  .object({ kind: ContextSourceKind, ref: z.string(), count: z.int(), bytes: z.int() })
  .meta({ id: "ContextSource" });
export type ContextSource = z.infer<typeof ContextSource>;

export const ReportHighlight = z.strictObject({
  occurred_at: Timestamp,
  type: DottedName,
  title: z.string(),
  attrs: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])),
});

/**
 * WORKFORCE-WEEK: what a DE did this week, from its record (`output.*`, `review.*`,
 * `task.completed`), plus a short line per helper when helper KPIs are on. Optional, and present
 * only when there is something to say (a snapshot taken before this existed has none).
 */
export const ReportWorkforce = z.strictObject({
  approved: z.int().min(0),
  edited: z.int().min(0),
  rejected: z.int().min(0),
  completed: z.int().min(0),
  /** FAILURE-STOP: runs stopped after repeated failures this week (absent in older reports). */
  failure_stops: z.int().min(0).optional(),
  /** FAILURE-STOP: tokens spent in the week's failed or stopped runs. */
  failed_tokens: z.int().min(0).optional(),
  helpers: z
    .array(
      z.strictObject({
        name: z.string(),
        lines: z.array(z.strictObject({ label: z.string(), value: z.int().min(0) })).max(3),
        cost_minor: z.int().nullable(),
      }),
    )
    .max(20),
});
export type ReportWorkforce = z.infer<typeof ReportWorkforce>;

export const FridayReport = z
  .strictObject({
    company: z.strictObject({
      id: Uuid,
      name: z.string(),
      reporting_currency: Currency,
      timezone: z.string(),
    }),
    week: z.strictObject({ key: WeekKey, starts_on: IsoDate, ends_on: IsoDate }),
    generated_at: Timestamp,
    version: z.int().min(1),
    summary: z.strictObject({
      work_done: z.number(),
      outcomes: z.number(),
      approval_rate: z.number().nullable(),
      cost_minor: z.int(),
      supervision_minutes: z.int(),
      band: Band,
    }),
    digital_employees: z.array(
      z.strictObject({
        membership_id: Uuid,
        display_name: z.string(),
        seat_title: z.string(),
        supervisor_name: z.string().nullable(),
        scorecard: ClientScorecard,
        highlights: z.array(ReportHighlight).max(10),
        workforce: ReportWorkforce.optional(),
      }),
    ),
    cost: z.strictObject({
      by_de: z.array(
        z.strictObject({
          membership_id: Uuid,
          subscription_minor: z.int(),
          usage_minor: z.int(),
          total_minor: z.int(),
        }),
      ),
      late_adjustments: z.array(
        z.strictObject({ week_key: WeekKey, delta_minor: z.int(), reason: z.string() }),
      ),
    }),
    next_week: z.array(
      z.strictObject({
        membership_id: Uuid,
        metric_key: z.string(),
        target: z.number().nullable(),
      }),
    ),
    supervisor_note: z.string().nullable(),
  })
  .meta({ id: "FridayReport" });
export type FridayReport = z.infer<typeof FridayReport>;
